import React, { useState, useEffect, useCallback, useRef } from "react";
import { withTranslation, useTranslation } from "react-i18next";
import CalendarSVG from "../../../assets/precheckin/calendar.svg";
import { WheelDiv } from "../GuestSteps/SecondStepStyle";
import WheelPicker from "react-simple-wheel-picker";
import { Modal } from "react-responsive-modal";
import moment from "moment";
import debounce from "lodash.debounce";

const days = Array.from({ length: 31 }, (_, i) => ({
  id: (i + 1).toString(),
  value: (i + 1).toString(),
}));

const months = moment.months().map((month, i) => ({
  id: (i + 1).toString(),
  value: month.charAt(0).toUpperCase() + month.slice(1),
}));

const hours = (props, date) => {
  const timeStart = moment(props.timeStart || "00:00", "HH:mm");
  const timeEnd = moment(props.timeEnd || "23:59", "HH:mm");
  const isToday = moment().isSame(moment(date), 'day');
  const fromHour = isToday ? moment().hour() : timeStart.hour()
  const endHour = timeEnd.minute() > 0 ? timeEnd.hour() + 1 : timeEnd.hour();

  const length = endHour - fromHour;

  const hrs = Array.from({ length }, (_, i) => {
    const hour = fromHour + i;
    const hourString = hour.toString().padStart(2, "0");
    return { id: hourString, value: hourString };
  });

  return hrs;
};

const twelveHours = Array.from({ length: 12 }, (_, i) => ({
  id: (i + 1).toString().padStart(2, "0"),
  value: (i + 1).toString().padStart(2, "0"),
}));

const minutes = (props) => {
  const step = props.step || 1;
  const length = step > 0 ? Math.ceil(60 / step) : 60;
  return Array.from({ length }, (_, i) => ({
    id: (step * i).toString().padStart(2, "0"),
    value: (step * i).toString().padStart(2, "0"),
  }));
};

const ampmData = [
  { id: "am", value: "AM" },
  { id: "pm", value: "PM" },
];

const DateTimePickerComponent = (props) => {
  const { t } = useTranslation();
  const arrivalDate = props.getDate || moment().format("YYYY-MM-DD");
  const arrivalTime = props.getTime || moment().format("HH:MM");
  const currentDateAndTime = arrivalDate + "T" + arrivalTime;
  const currentDate = moment(currentDateAndTime, "YYYY-MM-DDTHH:mm");

  const initialMinute = () => {
    let defaultMinute = currentDate.format("mm") || "00";
    if (
      minutes(props).filter((obj) => {
        return obj.id === defaultMinute;
      }).length === 0
    )
      defaultMinute = minutes(props)[0].id;
    return defaultMinute || "00";
  };

  const checkAmPm = props.ampm || false;
  const [calenderOpen, setCalenderOpen] = useState(false);
  const [day, setDay] = useState(currentDate.format("D") || "1");
  const [month, setMonth] = useState(currentDate.format("M") || "1");
  const [year, setYear] = useState(
    currentDate.format("YYYY") || props.fromYear || moment().year().toString()
  );
  const [hour, setHour] = useState(
    checkAmPm ? currentDate.format("hh") : currentDate.format("HH")
  );
  const [minute, setMinute] = useState(initialMinute() || "00");
  const [date, setDate] = useState(
    props.from && moment(props.from).format("YYYY-MM-DD")
  );
  const [ampm, setAmpm] = useState(currentDate.format("a"), "am");
  const [mainHours, setMainHours] = useState([]);
  const [mainHour, setMainHour] = useState();
  const [mainMinutes, setMainMinutes] = useState([]);
  const [mainMinute, setMainMinute] = useState();

  const onOpenCalendarModal = () => {
    resetData(props.getDate);
    setCalenderOpen(true);
  };

  const resetData = (date) => {
    let hoursList = props.ampm ? twelveHours : hours(props, date);
    const initialHour = availableData(hoursList, hour);
    const minutesList = updateMinutesBasedOnHour(initialHour);
  
    setMainHours(hoursList);
    setMainHour(initialHour);
    setMainMinutes(minutesList);
    setMainMinute(availableData(minutesList, minute));
  }

  const updateMinutesBasedOnHour = (selectedHour) => {
    const allMinutes = minutes(props);
    const endHour = moment(props.timeEnd || "23:59", "HH:mm").hour();
    const endMinute = moment(props.timeEnd || "23:59", "HH:mm").minute();
  
    // If the selected hour is the endHour, filter minutes
    if (parseInt(selectedHour, 10) === endHour) {
      return allMinutes.filter(minute => parseInt(minute.id, 10) < endMinute);
    }
  
    return allMinutes; // Otherwise, return all minutes
  };  
  
  const onCloseCalendarModal = () => {
    setCalenderOpen(false);
    const html = document.querySelector("html");
    html.style = "";
  };
  const handleYear = (target) => setYear(target.id);
  const handleMonth = (target) => setMonth(target.id);
  const handleDay = (target) => setDay(target.id);
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleHour = useCallback(
    debounce((target) => {
      const newHour = target.id;
      const updatedMinutes = updateMinutesBasedOnHour(newHour);
      setMainMinutes(updatedMinutes);
      setMainMinute(availableData(updatedMinutes, mainMinute));
      setHour(newHour);
    }, 300), // Adjust debounce delay as needed
    [mainMinute] // Dependencies
  );

  const handleMinute = (target) => setMinute(target.id);
  
  const previousDateRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleRangeOfDate = useCallback(
    debounce((target) => {
      if (previousDateRef.current === target.id) {
        return; // Prevent handling if the date hasn't changed
      }
      previousDateRef.current = target.id;

      const dt = moment(target.id);
      setYear(dt.year());
      setMonth(dt.month() + 1);
      setDay(dt.date());
      setDate(target.id);
      
      resetData(target.id)
    }, 300), // Adjust debounce delay as needed
    [] // No dependencies here, debounce will handle multiple calls
  );

  const handleAmpm = (target) => setAmpm(target.id);

  function range(start, end, reverse = false) {
    const data = Array(end - start + 1)
      .fill()
      .map((_, idx) => start + idx);
    return reverse ? data.reverse() : data;
  }

  const [years] = useState([
    ...range(
      props.fromYear || moment().year() - 150,
      props.toYear || moment().year(),
      (props.toYear || moment().year()) <= moment().year()
    ).map((year) => ({
      id: year.toString(),
      value: year.toString(),
    })),
  ]);

  const rangeOfDates = () => {
    const fromDate = moment(props.from || moment().format("YYYY-MM-DD"));
    const toDate = moment(props.to || moment().format("YYYY-MM-DD"));
    const currentTime = moment(); // Current time
    const endTimeToday = moment(`${props.from} ${props.timeEnd}`, "YYYY-MM-DD HH:mm").subtract(30, "minutes");
  
    let startDate = fromDate;
    if (currentTime.isAfter(endTimeToday)) {
      startDate = fromDate.add(1, "day"); // Skip today if past the threshold
    }
    const diff = toDate.diff(startDate, "days");
    let range = [];
  
    for (let i = 0; i <= diff; i++) {
      const date = startDate.clone().add(i, "day");
      range.push({
        id: date.format("YYYY-MM-DD"),
        value: date.format("DD MMM YYYY"),
      });
    }

    return range;
  };

  useEffect(() => {
    if (!props.allowBlank) selectCalendarBtn(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectCalendarBtn = (hideModal = true) => {
    const timeWithoutAmpm =
      props.dateType === "date"
        ? "23:59"
        : props.ampm && ampm === "pm"
        ? `${(parseInt(hour) + 12)
            .toString()
            .padStart(2, "0")}:${minute.padStart(2, "0")}`
        : `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
    const today = moment(moment().format("YYYY-MM-DD HH:mm"));
    const time =
      props.dateType === "date"
        ? `${today.hour()}:${today.minute()}`
        : `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}${
            props.ampm ? ` ${ampm.toUpperCase()}` : ""
          }`;
    const onlyDate = `${year}-${month.toString().padStart(2, "0")}-${day
      .toString()
      .padStart(2, "0")}`;
    const dateTime = `${onlyDate} ${timeWithoutAmpm}`;
    const toFormat = moment(dateTime);
    const currentTime = props.ampm
      ? moment(time + " " + ampm, "hh:mm a")
      : time;

    if (hideModal && props.present && toFormat < today) {
      alert("You cannot select time in the past!");
    } else if (hideModal && timeLimitRange(currentTime)) {
      alert(
        "You’ve selected a time outside of the availability window, please select an alternate time."
      );
    } else {
      if (hideModal) setCalenderOpen(false);
      setDate(onlyDate);
      const dateObj = {
        name: props.dateName || "date",
        value: toFormat.toISOString(),
        dateTime: dateTime,
      };
      props.handleChange({ target: dateObj });

      if (props.enableTime || props.onlyTime) {
        const timeObj = {
          name: props.timeName || "time",
          value: time,
          dateTime: dateTime,
        };
        props.handleChange({ target: timeObj });
      }
    }
  };

  const timeLimitRange = (timeSelect) => {
    const timeSelected = moment(timeSelect, "HH:mm");
    const timeStart = moment(props.timeStart, "HH:mm");
    const timeEnd = moment(props.timeEnd, "HH:mm");
    let unavailable = false;
    if (timeStart < timeEnd) {
      if (timeStart <= timeSelected && timeSelected <= timeEnd) {
      } else {
        unavailable = true;
      }
    } else if (timeStart > timeEnd) {
      if (
        (timeStart <= timeSelected && timeSelected > timeEnd) ||
        (timeStart > timeSelected && timeSelected <= timeEnd)
      ) {
      } else {
        unavailable = true;
      }
    }
    return unavailable;
  };

  const availableData = (data, id) => {
    const found = data.some((el) => (el.id === id ? true : false));
    return found ? id : data[0] && data[0].id;
  };

  const customCalendarClassNames = {
    closeButton: "close-button-modal",
    modal: "date-time-picker-modal",
    overlay: "overlay-modal",
  };

  const selectRangeOfDate = () => {
    return availableData(rangeOfDates(), date)
  }

  return (
    <div className="h-full">
      <div
        onClick={() => onOpenCalendarModal()}
        className={`mx-auto max-w-lg border rounded-full py-4 w-full px-4 outline-none focus:outline-none md:mb-10 flex justify-center cursor-pointer ${props.className}`}
        style={{ borderColor: props.borderColor || "#DBDBDB" }}>
        {!props.onlyTime && (
          <p
            className={`flex items-center mr-4 ${
              props.className && props.className
            }`}>
            <img
              src={CalendarSVG}
              alt="calendar"
              className="w-8 h-8 mr-4"
            />
            {props.allowBlank && !(props.getDate || date)
              ? props.format || "DD/MM/YYYY"
              : moment(props.getDate ? props.getDate : date).format(
                  props.format || "DD/MM/YYYY"
                ) ||
                props.format ||
                "DD/MM/YYYY"}
          </p>
        )}
        {props.enableTime && (
          <p className="flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 mr-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {hour + ":" + minute || "HH:MM"}
            {props.ampm && ` ${ampm.toUpperCase()}`}
          </p>
        )}
      </div>
      {props.timeStart !== props.timeEnd && (
        <div className="mx-auto text-center text-gray-400">
          {`${t("ITEM_AVAILABLE")}from ${props.timeStart} to ${props.timeEnd}.`}
        </div>
      )}
      <Modal
        open={calenderOpen}
        onClose={onCloseCalendarModal}
        center
        blockScroll={true}
        classNames={customCalendarClassNames}>
        <div className="relative h-full flex flex-col">
          <div className="mx-auto max-w-screen-md py-4">
            <h3 className="text-14 px-4 py-3">{props.modalTitle}</h3>
          </div>
          <hr className="mb-4" />
          <div className="flex mx-auto max-w-xl mb-4 flex-1">
            {!props.onlyTime && (
              <>
                <div className="flex items-center">
                  <img
                    src={CalendarSVG}
                    alt="calendar"
                    className="w-8"
                  />
                </div>
                {!props.from && !props.to && (
                  <>
                    <WheelDiv className="flex items-center">
                      <WheelPicker
                        name="calendar_month"
                        data={months}
                        onChange={handleMonth}
                        height={150}
                        titleText="Enter month"
                        itemHeight={30}
                        selectedID={availableData(months, month)}
                        color="#ccc"
                        activeColor="#333"
                        backgroundColor="#fff"
                        shadowColor="#fff"
                      />
                    </WheelDiv>
                    <WheelDiv className="flex items-center">
                      <WheelPicker
                        name="calendar_day"
                        data={days}
                        onChange={handleDay}
                        height={150}
                        titleText="Enter day"
                        itemHeight={30}
                        selectedID={availableData(days, day)}
                        color="#ccc"
                        activeColor="#333"
                        backgroundColor="#fff"
                        shadowColor="#fff"
                      />
                    </WheelDiv>
                    <WheelDiv className="flex items-center">
                      <WheelPicker
                        name="calendar_year"
                        data={years}
                        onChange={handleYear}
                        height={150}
                        titleText="Enter year"
                        itemHeight={30}
                        selectedID={availableData(years, year)}
                        color="#ccc"
                        activeColor="#333"
                        backgroundColor="#fff"
                        shadowColor="#fff"
                      />
                    </WheelDiv>
                  </>
                )}
                {props.from && props.to && (
                  <WheelDiv className="flex items-center">
                    <WheelPicker
                      name="calendar_month"
                      data={rangeOfDates()}
                      onChange={handleRangeOfDate}
                      height={150}
                      titleText="Enter date"
                      itemHeight={30}
                      selectedID={selectRangeOfDate()}
                      color="#ccc"
                      activeColor="#333"
                      backgroundColor="#fff"
                      shadowColor="#fff"
                    />
                  </WheelDiv>
                )}
              </>
            )}
            {props.enableTime && (
              <>
                <div className="flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <WheelDiv className="flex items-center">
                  {mainHour && (
                    <WheelPicker
                      name="calendar_hour"
                      data={mainHours}
                      onChange={handleHour}
                      height={150}
                      titleText="Enter hour"
                      itemHeight={30}
                      selectedID={mainHour}
                      color="#ccc"
                      activeColor="#333"
                      backgroundColor="#fff"
                      shadowColor="#fff"
                    />
                  )}
                </WheelDiv>
                <WheelDiv className="flex items-center">
                  {mainMinute && (
                    <WheelPicker
                      name="calendar_minute"
                      data={mainMinutes}
                      onChange={handleMinute}
                      height={150}
                      titleText="Enter minute"
                      itemHeight={30}
                      selectedID={mainMinute}
                      color="#ccc"
                      activeColor="#333"
                      backgroundColor="#fff"
                      shadowColor="#fff"
                    />
                  )}
                </WheelDiv>
                {props.ampm && (
                  <WheelDiv className="flex items-center">
                    <WheelPicker
                      name="calendar_ampm"
                      data={ampmData}
                      onChange={handleAmpm}
                      height={150}
                      titleText="Enter am/pm"
                      itemHeight={30}
                      selectedID={availableData(ampmData, ampm)}
                      color="#ccc"
                      activeColor="#333"
                      backgroundColor="#fff"
                      shadowColor="#fff"
                    />
                  </WheelDiv>
                )}
              </>
            )}
          </div>
          <div className="md:w-48 md:mx-auto">
            <button
              className={`app-button ${props.buttonStyleName} tablet-only-style-button`}
              style={{
                backgroundColor: props.primaryColor,
                color: props.appHomeTextCallToAction || "#ffffff",
                borderColor: props.borderColor || "#ffffff",
              }}
              onClick={selectCalendarBtn}>
              {t("SELECT")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default withTranslation()(DateTimePickerComponent);
